import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  Calendar, 
  Download, 
  Eye,
  Filter,
  RefreshCw,
  Target,
  Award,
  List,
  Users,
  TrendingUp,
  ExternalLink,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const EventResultsDashboard = () => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [groupStatistics, setGroupStatistics] = useState([]);
  const [evaluationItems, setEvaluationItems] = useState([]);
  const [itemResults, setItemResults] = useState([]);
  const [questionResponses, setQuestionResponses] = useState([]);
  const [detailedResponses, setDetailedResponses] = useState({ responses: [], questionResponses: [] });
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    fetchEvents();
    fetchEvaluationGroupStats();
    fetchEvaluationItems();
    fetchItemResults(); // Fetch individual item results on initial load
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await api.get('/simple.php?action=events');
      if (response.data.success) {
        setEvents(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvaluationItems = async () => {
    try {
      const response = await api.get('/simple.php?action=evaluation-items');
      if (response.data.success) {
        setEvaluationItems(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching evaluation items:', error);
    }
  };

  const fetchEvaluationGroupStats = async (eventId = null) => {
    try {
      setStatsLoading(true);
      
      const params = eventId ? `&event_id=${eventId}` : '';
      const response = await api.get(`/simple.php?action=evaluation-group-stats${params}`);
      
      if (response.data.success) {
        setGroupStatistics(response.data.data.groupStatistics);
        
        if (eventId) {
          fetchItemResults(eventId);
        }
      }
      
    } catch (error) {
      console.error('Error fetching evaluation group statistics:', error);
      toast.error('Failed to load evaluation statistics');
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchItemResults = async (eventId) => {
    try {
      const params = eventId ? `&event_id=${eventId}` : '';
      const response = await api.get(`/simple.php?action=evaluation-item-results${params}`);
      
      if (response.data.success) {
        setItemResults(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching item results:', error);
      toast.error('Failed to load item results');
    }
  };

  const fetchQuestionResponses = async (eventId) => {
    try {
      const response = await api.get(`/simple.php?action=event-question-responses&event_id=${eventId}`);
      
      if (response.data.success) {
        setQuestionResponses(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching question responses:', error);
      toast.error('Failed to load question responses');
    }
  };

  const fetchDetailedResponses = async (eventId) => {
    try {
      const response = await api.get(`/simple.php?action=event-session-responses&event_id=${eventId}`);
      if (response.data.success) {
        // PHP returns sessions with embedded question_answers; reshape to match expected format
        const sessions = response.data.data;
        const questionResponses = sessions.flatMap(s =>
          (s.question_answers || []).map(qa => ({ ...qa, session_id: s.session_id }))
        );
        setDetailedResponses({ responses: sessions, questionResponses });
      }
    } catch (error) {
      console.error('Error fetching detailed responses:', error);
    }
  };

  const handleEventFilter = (eventId) => {
    try {
      const event = eventId ? events.find(e => e.events_id === parseInt(eventId)) : null;
      setSelectedEvent(event);
      fetchEvaluationGroupStats(eventId);
      fetchItemResults(eventId);
      
      // Fetch question responses when an event is selected
      if (eventId) {
        fetchQuestionResponses(eventId);
        fetchDetailedResponses(eventId);
      } else {
        setQuestionResponses([]);
        setDetailedResponses({ responses: [], questionResponses: [] });
      }
    } catch (error) {
      console.error('Error in handleEventFilter:', error);
    }
  };

  const getRatingStyle = (rating) => {
    if (rating >= 3.8) return { color: '#16a34a', backgroundColor: '#dcfce7' };
    if (rating >= 3.2) return { color: '#2563eb', backgroundColor: '#dbeafe' };
    if (rating >= 2.8) return { color: '#d97706', backgroundColor: '#fef3c7' };
    if (rating >= 2.4) return { color: '#ea580c', backgroundColor: '#fed7aa' };
    return { color: '#dc2626', backgroundColor: '#fecaca' };
  };

  const getRatingLabel = (rating) => {
    if (rating >= 3.8) return 'Excellent';
    if (rating >= 3.2) return 'Very Good';
    if (rating >= 2.8) return 'Good';
    if (rating >= 2.4) return 'Fair';
    return 'Needs Improvement';
  };

  const getEventEvaluationUrl = (eventId) => {
    return `${window.location.origin}/evaluation/${eventId}`;
  };

  const handleDownload = (format) => {
    if (!selectedEvent) {
      toast.error('Please select an event first');
      return;
    }
    // Use PHP API in production, Node.js API locally
    const isProduction = !window.location.hostname.includes('localhost');
    const url = isProduction
      ? `/php-api/simple.php?action=export&event_id=${selectedEvent.events_id}&format=${format}`
      : `/api/admin/events/${selectedEvent.events_id}/export?format=${format}`;
    const link = document.createElement('a');
    link.href = url;
    link.download = '';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #e5e7eb',
          borderTop: '4px solid #3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p style={{ color: '#6b7280' }}>Loading evaluation dashboard...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Compact Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        borderRadius: '12px', 
        padding: '20px', 
        marginBottom: '20px',
        color: 'white'
      }}>
        <h1 style={{ 
          fontSize: '1.8rem', 
          margin: '0 0 8px 0', 
          fontWeight: '700',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Target size={28} />
          Event Evaluation Dashboard
        </h1>
        <p style={{ 
          margin: 0, 
          opacity: 0.9,
          fontSize: '14px'
        }}>
          Evaluation Group Analytics
        </p>
      </div>

      {/* Compact Filter */}
      <div style={{ 
        background: '#ffffff', 
        borderRadius: '8px', 
        padding: '12px', 
        marginBottom: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Filter size={16} style={{ color: '#6b7280' }} />
            <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>Filter:</span>
          </div>
          <select
            value={selectedEvent?.events_id || ''}
            onChange={(e) => {
              const value = e.target.value;
              handleEventFilter(value === '' ? null : value);
            }}
            style={{
              padding: '6px 10px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '13px',
              background: '#ffffff',
              color: '#374151',
              cursor: 'pointer'
            }}
          >
            <option value="">All Events</option>
            {events.map((event) => (
              <option key={event.events_id} value={event.events_id}>
                {event.event_name}
              </option>
            ))}
          </select>
          <button
            onClick={() => {
              setSelectedEvent(null);
              setItemResults([]);
              setQuestionResponses([]);
              fetchEvaluationGroupStats(null);
            }}
            style={{
              padding: '6px 10px',
              background: '#f3f4f6',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '13px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <RefreshCw size={12} />
            Reset
          </button>

          {selectedEvent && (
            <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
              <button
                onClick={() => handleDownload('pdf')}
                title="Download PDF Summary"
                style={{
                  padding: '6px 12px',
                  background: '#dc2626',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                <Download size={13} />
                PDF
              </button>
              <button
                onClick={() => handleDownload('excel')}
                title="Download Excel Report"
                style={{
                  padding: '6px 12px',
                  background: '#16a34a',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                <Download size={13} />
                Excel
              </button>
              <button
                onClick={() => handleDownload('csv')}
                title="Download CSV"
                style={{
                  padding: '6px 12px',
                  background: '#2563eb',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                <Download size={13} />
                CSV
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Loading State */}
      {statsLoading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{
            width: '32px',
            height: '32px',
            border: '3px solid #e5e7eb',
            borderTop: '3px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 12px'
          }} />
          <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading evaluation statistics...</p>
        </div>
      ) : (
        <>
          {/* Compact Evaluation Groups Performance */}
          <div style={{ 
            background: '#ffffff', 
            borderRadius: '8px', 
            padding: '16px', 
            marginBottom: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            border: '1px solid #e5e7eb'
          }}>
            <h2 style={{ 
              fontSize: '1.2rem', 
              marginBottom: '12px', 
              color: '#1f2937',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Target size={18} />
              Evaluation Groups Performance
            </h2>
            
            {groupStatistics.length > 0 ? (
              <div style={{ display: 'grid', gap: '16px' }}>
                {groupStatistics.map((group, index) => {
                  // Get items for this group
                  const groupItems = itemResults.filter(item => 
                    item.evaluation_group === group.evaluation_group
                  );
                  
                  return (
                    <div key={index} style={{
                      padding: '16px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      background: '#ffffff'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <h3 style={{ 
                          fontSize: '16px', 
                          margin: 0, 
                          color: '#1f2937',
                          fontWeight: '600'
                        }}>
                          {group.evaluation_group}
                        </h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            ...getRatingStyle(parseFloat(group.average_rating) || 0)
                          }}>
                            {getRatingLabel(parseFloat(group.average_rating) || 0)}
                          </span>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '6px',
                            background: '#3b82f6',
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}>
                            <Award size={14} />
                            {parseFloat(group.average_rating || 0).toFixed(2)}
                          </div>
                        </div>
                      </div>
                      
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#6b7280',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <Users size={12} />
                        {group.response_count} respondents
                      </div>
                      
                      {/* Individual Items - Always show, even if no responses */}
                      <div style={{ 
                        background: '#f8fafc',
                        borderRadius: '6px',
                        padding: '12px',
                        border: '1px solid #e2e8f0'
                      }}>
                        {groupItems.length > 0 ? (
                          <div style={{ display: 'grid', gap: '6px' }}>
                            {groupItems.map((item, itemIndex) => (
                              <div key={item.evaluation_item_id} style={{
                                padding: '6px 8px',
                                background: '#ffffff',
                                borderRadius: '4px',
                                border: '1px solid #e2e8f0'
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{
                                    fontSize: '12px',
                                    color: '#374151',
                                    flex: 1
                                  }}>
                                    {itemIndex + 1}. {item.evaluation_item}
                                  </span>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{
                                      fontSize: '10px',
                                      color: '#6b7280'
                                    }}>
                                      ({item.response_count} respondents)
                                    </span>
                                    <span style={{
                                      padding: '2px 6px',
                                      borderRadius: '6px',
                                      fontSize: '11px',
                                      fontWeight: '600',
                                      marginLeft: '8px',
                                      ...getRatingStyle(parseFloat(item.average_rating) || 0)
                                    }}>
                                      {parseFloat(item.average_rating || 0).toFixed(2)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p style={{
                            fontSize: '12px',
                            color: '#6b7280',
                            margin: 0,
                            fontStyle: 'italic',
                            textAlign: 'center',
                            padding: '8px'
                          }}>
                            No evaluation items found for this group
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
                No evaluation data available. Select an event to view statistics.
              </p>
            )}
          </div>

          {/* Event Question Responses Section */}
          {selectedEvent && (
            <div style={{ 
              background: '#ffffff', 
              borderRadius: '8px', 
              padding: '16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              border: '1px solid #e5e7eb'
            }}>
              <h2 style={{ 
                fontSize: '1.2rem', 
                marginBottom: '12px', 
                color: '#1f2937',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <List size={18} />
                Question Responses for {selectedEvent.event_name}
              </h2>
              
              <div style={{ 
                background: '#f8fafc', 
                borderRadius: '8px', 
                padding: '16px',
                border: '1px solid #e2e8f0'
              }}>
                {questionResponses.length > 0 ? (
                  <div style={{ display: 'grid', gap: '16px' }}>
                    {questionResponses.map((questionData, index) => (
                      <div 
                        key={questionData.question_id}
                        style={{
                          padding: '16px',
                          background: '#ffffff',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0'
                        }}
                      >
                        <div style={{ marginBottom: '12px' }}>
                          <h4 style={{ 
                            fontSize: '14px', 
                            fontWeight: '600',
                            color: '#1f2937',
                            margin: '0 0 8px 0'
                          }}>
                            {index + 1}. {questionData.question_text}
                          </h4>
                          <span style={{
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '500',
                            background: '#dbeafe',
                            color: '#1e40af'
                          }}>
                            {questionData.responses.length} response{questionData.responses.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        
                        {questionData.responses.length > 0 ? (
                          <div style={{ display: 'grid', gap: '8px' }}>
                            {questionData.responses.map((response, responseIndex) => (
                              <div 
                                key={responseIndex}
                                style={{
                                  padding: '8px 12px',
                                  background: '#f8fafc',
                                  borderRadius: '6px',
                                  border: '1px solid #e2e8f0'
                                }}
                              >
                                <div style={{ 
                                  fontSize: '13px', 
                                  color: '#374151',
                                  marginBottom: '4px',
                                  lineHeight: '1.4'
                                }}>
                                  {response.response_text}
                                </div>
                                <div style={{
                                  fontSize: '11px',
                                  color: '#9ca3af',
                                  textAlign: 'right',
                                  marginTop: '4px'
                                }}>
                                  {new Date(response.submitted_at).toLocaleDateString()}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p style={{ 
                            color: '#6b7280', 
                            fontSize: '13px',
                            fontStyle: 'italic',
                            margin: 0 
                          }}>
                            No responses yet
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ 
                    color: '#6b7280', 
                    textAlign: 'center', 
                    padding: '20px',
                    margin: 0 
                  }}>
                    No questions configured for this event
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Individual Evaluations with Question Responses */}
          {selectedEvent && detailedResponses.responses.length > 0 && (
            <div style={{ 
              background: '#ffffff', 
              borderRadius: '8px', 
              padding: '16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              border: '1px solid #e5e7eb'
            }}>
              <h2 style={{ 
                fontSize: '1.2rem', 
                marginBottom: '12px', 
                color: '#1f2937',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Users size={18} />
                Individual Evaluations ({detailedResponses.responses.length})
              </h2>

              <div style={{ display: 'grid', gap: '12px' }}>
                {detailedResponses.responses.map((session) => {
                  const sessionAnswers = detailedResponses.questionResponses.filter(
                    (qr) => qr.session_id === session.session_id
                  );
                  return (
                    <div
                      key={session.session_id}
                      style={{
                        background: '#f8fafc',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        padding: '14px'
                      }}
                    >
                      {/* Respondent header */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: sessionAnswers.length > 0 ? '12px' : 0
                      }}>
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '14px', color: '#1f2937' }}>
                            {session.first_name} {session.last_name}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                            {session.division_name}{session.sex ? ` · ${session.sex}` : ''}
                          </div>
                        </div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', textAlign: 'right' }}>
                          {new Date(session.submitted_at).toLocaleDateString('en-US', {
                            year: 'numeric', month: 'short', day: 'numeric'
                          })}
                        </div>
                      </div>

                      {/* Question answers for this respondent */}
                      {sessionAnswers.length > 0 && (
                        <div style={{ display: 'grid', gap: '8px', borderTop: '1px solid #e2e8f0', paddingTop: '10px' }}>
                          <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
                            Open-ended Responses
                          </div>
                          {sessionAnswers.map((answer, i) => (
                            <div key={i} style={{ background: '#ffffff', borderRadius: '6px', border: '1px solid #e2e8f0', padding: '10px 12px' }}>
                              <div style={{ fontSize: '12px', fontWeight: '600', color: '#4b5563', marginBottom: '4px' }}>
                                {answer.question_text}
                              </div>
                              <div style={{ fontSize: '13px', color: '#374151', lineHeight: '1.5' }}>
                                {answer.response_text || <em style={{ color: '#9ca3af' }}>No response</em>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EventResultsDashboard;